// ============================================================================
// Waniwani Chat Embed — IIFE entry point
//
// Drop a <script> tag with `data-token` on any page; the chat mounts inside
// the first `[data-waniwani-embed]` element on the page, rendered inside a
// shadow DOM for style isolation.
// ============================================================================

// First, and before anything else in the bundle: `boot-clock` reads the clock
// and the page's pre-existing globals on the first line the IIFE runs.
import "./boot-clock";
import type { UIMessage } from "ai";
import React from "react";
import ReactDOM from "react-dom/client";
import type { TrackFn, TrackInput } from "../../../tracking/@types";
import {
	createChatTrackClient,
	createNoopChatTrackClient,
} from "../lib/chat-track";
import {
	applyVisitorId,
	getOrCreateVisitorId,
	setVisitorId,
} from "../lib/visitor-context";
import { startAgentBeacon } from "./agent-beacon";
import { ComposerChat, type ComposerChatHandle } from "./composer-chat";
import type { EmbedConfig } from "./config";
import {
	DEFAULT_EMBED_HEIGHT,
	findScriptTag,
	parseConfigFromScript,
	resolveConfig,
} from "./config";
import { injectEmbedCss } from "./embed-css";
import { FloatingChat, type FloatingChatHandle } from "./floating-chat";
import { InlineChat, type InlineChatHandle } from "./inline-chat";
import { loadCachedConfig } from "./remote-config";
import { isVisibleForPath } from "./visibility";
import { startWebMcp, type WebMcpHandle } from "./webmcp-bootstrap";

// ---------------------------------------------------------------------------
// Global type augmentation
// ---------------------------------------------------------------------------

declare global {
	interface Window {
		WaniWani?: {
			chat?: {
				init: (options?: Partial<EmbedConfig>) => EmbedInstance;
				destroy: () => void;
				/** Open the chat panel. No-op in inline mode. */
				open: () => void;
				/** Close the chat panel. No-op in inline mode. */
				close: () => void;
				/** Toggle the chat panel. No-op in inline mode. */
				toggle: () => void;
				/**
				 * Submit a user message to the chat. No-op if the embed has not
				 * mounted yet or the inner layout has not attached. In floating and
				 * composer mode this also opens the panel.
				 */
				sendMessage: (text: string) => void;
				/**
				 * Submit a user message and resolve with the final assistant
				 * message once streaming completes. Resolves with `undefined`
				 * if the embed has not mounted yet.
				 */
				sendMessageAndWait: (text: string) => Promise<UIMessage | undefined>;
				/** Clear all messages and start a fresh conversation. */
				reset: () => void;
				/**
				 * Clear the conversation without the confirmation dialog and emit
				 * `conversation.restarted`.
				 */
				restart: () => void;
				/**
				 * Copy the conversation as plain text. Resolves `false` when the
				 * browser refuses or the embed has not mounted.
				 */
				copyConversation: () => Promise<boolean>;
				/** Focus the chat input. */
				focus: () => void;
				/** Snapshot of the current chat messages. Empty until mounted. */
				getMessages: () => UIMessage[];
				/** Session ID for event correlation. Undefined until the first message. */
				getSessionId: () => string | undefined;
				/**
				 * Track a funnel event from the host page, with the chat session
				 * attached automatically. Same surface as the server client:
				 * `track({ event, properties })` plus the flat revenue helpers
				 * (`track.converted({ amount, currency })`, ...).
				 */
				track: TrackFn;
				/** Tie the visitor to a stable user id (emits `user.identified`). */
				identify: (
					userId: string,
					traits?: Record<string, unknown>,
				) => Promise<{ eventId: string }>;
				/**
				 * Override the anonymous visitor id with one you already track (a
				 * PostHog / Amplitude / Segment distinct id, your own cookie, …).
				 * Persists in `localStorage` and is sent on every later chat
				 * request and tracking event, so Waniwani correlates to the same
				 * visitor and server-side MCP tools read it as
				 * `context.waniwani.visitorId`. Safe to call before `init()` and
				 * as soon as an async analytics id resolves. Returns the effective
				 * id (a blank value is ignored, keeping the current id).
				 */
				setVisitorId: (id: string) => string;
				/** Read the visitor id the widget sends on each request. */
				getVisitorId: () => string;
			};
		};
	}
}

// ---------------------------------------------------------------------------
// Instance tracking
// ---------------------------------------------------------------------------

interface EmbedInstance {
	destroy: () => void;
	open: () => void;
	close: () => void;
	toggle: () => void;
	sendMessage: (text: string) => void;
	sendMessageAndWait: (text: string) => Promise<UIMessage | undefined>;
	reset: () => void;
	restart: () => void;
	copyConversation: () => Promise<boolean>;
	focus: () => void;
	getMessages: () => UIMessage[];
	getSessionId: () => string | undefined;
	/** Track a funnel event with the chat session attached automatically. */
	track: TrackFn;
	/** Tie the visitor to a stable user id (emits `user.identified`). */
	identify: (
		userId: string,
		traits?: Record<string, unknown>,
	) => Promise<{ eventId: string }>;
	/** Override the anonymous visitor id with one you already track. */
	setVisitorId: (id: string) => string;
	/** Read the visitor id the widget sends on each request. */
	getVisitorId: () => string;
}

/**
 * What the mount functions return; `init()` adds the tracking surface. The
 * visitor-id helpers operate on `localStorage`, not the mount, so `init()`
 * layers them on rather than each mount path re-implementing them.
 */
type MountedEmbed = Omit<
	EmbedInstance,
	"track" | "identify" | "setVisitorId" | "getVisitorId"
>;

let currentInstance: EmbedInstance | null = null;
let reactRoot: ReactDOM.Root | null = null;
let hostElement: HTMLElement | null = null;
// Second, body-level host used by `composer` mode for the overlay panel. The
// in-flow composer lives in `hostElement`; the panel is portaled here so a
// transformed ancestor in the host's markup can't become its containing block.
let panelHostElement: HTMLElement | null = null;
let containerResizeObserver: ResizeObserver | null = null;

// ---------------------------------------------------------------------------
// Container defaults — injected into the light DOM so customers can override
// with normal CSS specificity.
// ---------------------------------------------------------------------------

const SIZING_STYLE_ID = "waniwani-chat-sizing";
const STRUCTURAL_STYLE_ID = "waniwani-chat-defaults";
const CHROME_STYLE_ID = "waniwani-chat-chrome";
const AUTO_CARD_STYLE_ID = "waniwani-chat-auto-card";

// Sizing default — always injected for inline embeds so a bare
// `<div data-waniwani-embed>` (or the one we auto-create) is bounded out of
// the box instead of growing with content. `:where()` keeps specificity at
// 0, so *any* author rule wins: `[data-waniwani-embed]{height:400px}`,
// `max-height`, a flex/grid track, or `height:auto` to go fully fluid.
// `data-height` sets it explicitly via an inline style (see `mountInline`).
const SIZING_DEFAULTS_CSS = `:where([data-waniwani-embed]){height:${DEFAULT_EMBED_HEIGHT}}`;

// Card-shape defaults — applied only when a preset is chosen. Without one,
// we touch nothing on the customer's container so they keep their own
// shape. `:where()` keeps specificity at 0 so any normal customer selector
// wins (e.g. `border-radius: 0` to opt out of the rounding).
// No `background` here on purpose: the inner chat draws its own background
// via `--ww-color-background`, which switches with the active preset
// (light/dark/auto). Setting a light background here would show through
// any rounded-corner gap in dark mode.
const STRUCTURAL_DEFAULTS_CSS = `:where([data-waniwani-embed]){border-radius:var(--ww-radius,16px);overflow:hidden}`;

// Chrome defaults (border, shadow). Always injected because the vars
// default to no-op values — invisible until the customer passes
// `appearance.variables.borderWidth` / `boxShadow` (bridged onto the
// container by `applyContainerAppearance`) or sets `--ww-*` themselves.
// Lives on the container (not the chat root) because the container's
// `overflow:hidden` would clip a shadow drawn inside.
const CONTAINER_CHROME_CSS = `:where([data-waniwani-embed]){border-style:solid;border-width:var(--ww-border-width,0);border-color:var(--ww-border,transparent);box-shadow:var(--ww-shadow,none)}`;

// Card defaults for the container *we* auto-create (marked `data-waniwani-auto`).
// When the customer placed their own `[data-waniwani-embed]`, we respect their
// layout — but a container we inject has no styling of its own, and a bare
// block div would stretch full-width. So we present it as a centered, rounded
// card with a subtle border + shadow. Injected after `CONTAINER_CHROME_CSS`
// so its (equal-specificity) `:where()` border/shadow win, while still using
// the same `--ww-*` vars so customer `appearance.variables` override them.
// `:where()` => any author rule targeting `[data-waniwani-embed]` still wins.
const AUTO_CARD_CSS = `:where([data-waniwani-embed][data-waniwani-auto]){width:100%;max-width:28rem;margin-inline:auto;border-radius:var(--ww-radius,16px);overflow:hidden;border-style:solid;border-width:var(--ww-border-width,1px);border-color:var(--ww-border,rgba(0,0,0,0.1));box-shadow:var(--ww-shadow,0 10px 30px rgba(0,0,0,0.08))}`;

// Card defaults for a container we auto-create in `composer` mode. The
// composer draws its own border, radius and shadow, so this only bounds and
// centers the block — no chrome of its own to double up, and no height (the
// composer is content-sized).
const COMPOSER_AUTO_STYLE_ID = "waniwani-chat-composer-auto";
const COMPOSER_AUTO_CSS = `:where([data-waniwani-embed][data-waniwani-auto]){width:100%;max-width:40rem;margin-inline:auto}`;

function ensureStyle(id: string, css: string): void {
	if (typeof document === "undefined" || document.getElementById(id)) {
		return;
	}
	const style = document.createElement("style");
	style.id = id;
	style.textContent = css;
	document.head.appendChild(style);
}

// Mirror the container-level appearance vars onto the customer's container
// element. The chat root inside a shadow root can't push CSS vars back up to
// light DOM, so border, radius and shadow live on the container itself. Only the few
// vars that actually affect the container are bridged — colors etc. stay
// scoped to the chat root.
function applyContainerAppearance(
	container: Element,
	config: EmbedConfig,
): void {
	const vars = config.appearance?.variables;
	if (!vars || !(container instanceof HTMLElement)) {
		return;
	}
	if (typeof vars.borderWidth === "number") {
		container.style.setProperty("--ww-border-width", `${vars.borderWidth}px`);
	}
	if (typeof vars.borderRadius === "number") {
		container.style.setProperty("--ww-radius", `${vars.borderRadius}px`);
	}
	if (vars.borderColor) {
		container.style.setProperty("--ww-border", vars.borderColor);
	}
	if (vars.boxShadow) {
		container.style.setProperty("--ww-shadow", vars.boxShadow);
	}
}

// ---------------------------------------------------------------------------
// Shadow-root CSS injection
// ---------------------------------------------------------------------------

function injectStyles(shadowRoot: ShadowRoot, config: EmbedConfig): void {
	injectEmbedCss(shadowRoot);

	// In the embed.js path the outer `[data-waniwani-embed]` container draws
	// panel border + shadow (see `CONTAINER_CHROME_CSS`). CSS variables
	// inherit through the shadow boundary, so without this reset the chat
	// root's own chrome rule (`border-width: var(--ww-border-width, 0)`)
	// would render the same border/shadow again — visibly doubling them.
	// Two key properties of this fix:
	//   - It lives in the shadow root only, so the React paths
	//     (`<WaniwaniChat>`, `<ChatEmbed>`) still draw chrome on the chat
	//     root (the React surfaces have no outer container to delegate to).
	//   - It zeroes `border-width` / `border-radius` / `box-shadow` directly. The chat root
	//     has no inline `border-width` (only `--ww-border-width` as a
	//     custom property), so a later same-specificity rule wins by
	//     cascade order — no `!important` and no JS-side list of
	//     "container vars" to keep in sync.
	//   - `--ww-input-radius` is pinned to the 0.5rem the script embed has
	//     always rendered (its build concatenates utility classes, so
	//     `rounded-lg` won over the React path's `rounded-2xl`). A `data-css`
	//     stylesheet is appended after this reset, so it still overrides it.
	const chromeReset = document.createElement("style");
	chromeReset.textContent =
		"[data-waniwani-chat]{border-width:0;border-radius:0;box-shadow:none;--ww-input-radius:0.5rem}";
	shadowRoot.appendChild(chromeReset);

	if (config.css) {
		try {
			const cssUrl = new URL(config.css, window.location.href);
			if (cssUrl.protocol === "https:" || cssUrl.protocol === "http:") {
				const link = document.createElement("link");
				link.rel = "stylesheet";
				link.href = cssUrl.href;
				shadowRoot.appendChild(link);
			} else {
				console.warn(
					"[Waniwani] Custom CSS URL must use http or https protocol.",
				);
			}
		} catch {
			console.warn("[Waniwani] Invalid custom CSS URL:", config.css);
		}
	}
}

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

const INLINE_MARKER_ATTR = "data-waniwani-embed";
// Marks a container we created (vs. one the customer placed). Drives the
// centered-card defaults so an auto-injected container doesn't render as a
// bare full-width block.
const AUTO_MARKER_ATTR = "data-waniwani-auto";

// Find the inline mount target, creating one if the page has none. A bare
// snippet (just the `<script>` tag, no markup) then works out of the box:
// we insert `<div data-waniwani-embed>` immediately before the script so the
// chat mounts where the tag sits. Falls back to `document.body` when the
// script lives in `<head>` (a div there would never render) or can't be
// located.
function ensureInlineContainer(scriptEl: HTMLScriptElement | null): Element {
	const existing = document.querySelector(`[${INLINE_MARKER_ATTR}]`);
	if (existing) {
		return existing;
	}
	const container = document.createElement("div");
	container.setAttribute(INLINE_MARKER_ATTR, "");
	// Tag it as ours so `AUTO_CARD_CSS` applies (centered rounded card).
	container.setAttribute(AUTO_MARKER_ATTR, "");
	const inHead = scriptEl ? !!document.head?.contains(scriptEl) : false;
	if (scriptEl?.parentNode && !inHead) {
		scriptEl.parentNode.insertBefore(container, scriptEl);
	} else {
		document.body.appendChild(container);
	}
	return container;
}

// Normalize a `data-height` value: a bare number is treated as pixels,
// any other CSS length (`"80vh"`, `"600px"`, …) is passed through.
function normalizeHeight(value: string): string {
	return /^\d+$/.test(value.trim()) ? `${value.trim()}px` : value.trim();
}

function mountInline(
	config: EmbedConfig,
	programmatic: Partial<EmbedConfig> | undefined,
	scriptConfig: Partial<EmbedConfig> | undefined,
	scriptEl: HTMLScriptElement | null,
): MountedEmbed {
	const container = ensureInlineContainer(scriptEl);

	// Size the chat against the customer's container in two complementary
	// ways:
	//
	//   1. `height: 100%; max-height: inherit` — works when the customer's
	//      container has a definite height. CSS inheritance reaches across
	//      the shadow boundary via the composed tree.
	//   2. `flex: 1 1 auto; min-height: 0` + `display: flex; flex-direction:
	//      column` — works when the customer's container is a flex column
	//      bounded only by `max-height`. The host fills the flex-resolved
	//      space; declaring the host itself as a flex column lets the chat
	//      root become its flex child, which sidesteps a shadow-DOM quirk
	//      where `height: 100%` on a mount inside the shadow does not
	//      resolve against a flex-sized host. The mount uses `display:
	//      contents` so it drops out of layout entirely.
	//   3. A `ResizeObserver` reflects the container's *maximum* content
	//      area onto the host as an inline `max-height`. `max-height:
	//      inherit` copies the parent's value verbatim, so a `max-height:
	//      800px; padding: 24px; box-sizing: border-box` parent lets the
	//      chat overflow by the padding amount. Recomputing from the
	//      parent's CSS each time it resizes closes that gap. We read
	//      `max-height` / `height` (not the current `contentBoxSize`) to
	//      avoid a feedback loop: when the chat is shorter than the
	//      parent's bound, the parent's content-box shrinks to fit, and
	//      mirroring that would lock the host at its current size.

	// A default height so the embed is bounded out of the box (see
	// `SIZING_DEFAULTS_CSS`). `:where()` => any author CSS wins; `data-height`
	// sets it explicitly via an inline style, which beats the `:where()`
	// default without `!important`.
	ensureStyle(SIZING_STYLE_ID, SIZING_DEFAULTS_CSS);
	if (config.height && container instanceof HTMLElement) {
		container.style.height = normalizeHeight(config.height);
	}

	// Card shape (`border-radius`, `overflow`) is opt-in via `data-theme` /
	// `appearance.theme`. Without a preset, we leave the container's shape
	// alone. When opted in, the rule is wrapped in `:where()` so any customer
	// override on `[data-waniwani-embed]` still wins.
	if (config.appearance?.theme) {
		ensureStyle(STRUCTURAL_STYLE_ID, STRUCTURAL_DEFAULTS_CSS);
	}
	ensureStyle(CHROME_STYLE_ID, CONTAINER_CHROME_CSS);
	// A container we created presents as a centered rounded card. Injected
	// after the chrome rule so its border/shadow defaults win (both `:where()`,
	// equal specificity → source order decides).
	if (container.hasAttribute(AUTO_MARKER_ATTR)) {
		ensureStyle(AUTO_CARD_STYLE_ID, AUTO_CARD_CSS);
	}
	applyContainerAppearance(container, config);

	hostElement = document.createElement("div");
	hostElement.id = "waniwani-chat-embed";
	// Structural sizing only. `min-height: 0` is the flex-shrink unblocker:
	// when the customer's container is a flex column, this lets the host
	// (and the chat inside it) shrink below content size instead of
	// expanding the parent. The default height (500px) lives on the
	// container itself (see `SIZING_DEFAULTS_CSS`), not here.
	hostElement.style.cssText =
		"width:100%;height:100%;max-height:inherit;" +
		"display:flex;flex-direction:column;flex:1 1 auto;min-height:0;";
	container.appendChild(hostElement);

	const host = hostElement;
	const syncMaxHeight = () => {
		const cs = getComputedStyle(container);
		// Only mirror `max-height`. `getComputedStyle().height` returns the
		// resolved size — when the parent is bounded by `max-height` alone
		// and content is shorter, that resolved size shrinks to content,
		// and using it would lock the host below the parent's true bound.
		// When the parent uses an explicit `height`, the existing
		// `height: 100%` chain already resolves correctly against its
		// content box, so we leave the cap unset.
		//
		// Bail unless we get a concrete pixel value: percentages with an
		// indefinite containing block come back as `"80%"`, which would
		// otherwise be parsed as `80` pixels and severely shrink the chat.
		// `max-height: inherit` on the host handles those cases natively.
		if (!cs.maxHeight.endsWith("px")) {
			host.style.removeProperty("max-height");
			return;
		}
		const outer = Number.parseFloat(cs.maxHeight);
		if (!Number.isFinite(outer)) {
			host.style.removeProperty("max-height");
			return;
		}
		let inner = outer;
		if (cs.boxSizing === "border-box") {
			inner -=
				(Number.parseFloat(cs.paddingTop) || 0) +
				(Number.parseFloat(cs.paddingBottom) || 0) +
				(Number.parseFloat(cs.borderTopWidth) || 0) +
				(Number.parseFloat(cs.borderBottomWidth) || 0);
		}
		if (inner > 0) {
			host.style.maxHeight = `${inner}px`;
		} else {
			// Padding + border exceed the parent's `max-height`. Clear any
			// stale override so the host falls back to `max-height: inherit`
			// rather than keeping a value from an earlier observation.
			host.style.removeProperty("max-height");
		}
	};
	syncMaxHeight();
	containerResizeObserver = new ResizeObserver(syncMaxHeight);
	containerResizeObserver.observe(container);

	const shadowRoot = hostElement.attachShadow({ mode: "open" });
	injectStyles(shadowRoot, config);

	const mountContainer = document.createElement("div");
	mountContainer.className = "ww:contents";
	shadowRoot.appendChild(mountContainer);

	const inlineRef = React.createRef<InlineChatHandle>();

	// Collapse the container (which we own, outside React) on gated pages so it
	// shows no empty card. Pre-hide synchronously from the sessionStorage cache
	// so repeat visits to a gated page don't flash before the fetch resolves;
	// `onVisibilityChange` then keeps it in sync (incl. SPA route changes).
	const toggleContainer = (vis: boolean) => {
		if (container instanceof HTMLElement) {
			container.style.display = vis ? "" : "none";
		}
	};
	if (config.token) {
		const cached = loadCachedConfig(
			config.api ?? "",
			config.token,
			config.channelId,
		);
		if (
			cached?.visibility &&
			!isVisibleForPath(cached.visibility, window.location.pathname)
		) {
			toggleContainer(false);
		}
	}

	reactRoot = ReactDOM.createRoot(mountContainer);
	reactRoot.render(
		React.createElement(InlineChat, {
			ref: inlineRef,
			config,
			programmatic,
			scriptConfig,
			onVisibilityChange: toggleContainer,
		}),
	);

	return {
		destroy: () => {
			containerResizeObserver?.disconnect();
			containerResizeObserver = null;
			reactRoot?.unmount();
			reactRoot = null;
			hostElement?.remove();
			hostElement = null;
			// Remove the container too, but only if WE created it — leave any
			// author-placed `[data-waniwani-embed]` element in the page so
			// `destroy()` doesn't strip the host's own markup.
			if (container.hasAttribute(AUTO_MARKER_ATTR)) {
				container.remove();
			}
			currentInstance = null;
		},
		// Inline mode has no panel to open — these are no-ops so the public
		// API is uniform across modes.
		open: () => {},
		close: () => {},
		toggle: () => {},
		sendMessage: (text: string) => inlineRef.current?.chat?.sendMessage(text),
		sendMessageAndWait: async (text: string) => {
			const chat = inlineRef.current?.chat;
			if (!chat) {
				return undefined;
			}
			return (await chat.sendMessageAndWait(text)) as UIMessage | undefined;
		},
		reset: () => inlineRef.current?.chat?.reset(),
		restart: () => inlineRef.current?.chat?.restart(),
		copyConversation: async () =>
			(await inlineRef.current?.chat?.copyConversation()) ?? false,
		focus: () => inlineRef.current?.chat?.focus(),
		getMessages: () => inlineRef.current?.chat?.messages ?? [],
		getSessionId: () => inlineRef.current?.chat?.sessionId,
	};
}

// ---------------------------------------------------------------------------
// Mount — floating
// ---------------------------------------------------------------------------

function mountFloating(
	config: EmbedConfig,
	programmatic: Partial<EmbedConfig> | undefined,
	scriptConfig: Partial<EmbedConfig> | undefined,
): MountedEmbed {
	// A viewport-sized, click-through overlay appended to <body>. The launcher
	// and panel inside set `pointer-events: auto`, so the host page stays
	// interactive everywhere else. No `[data-waniwani-embed]` element needed.
	hostElement = document.createElement("div");
	hostElement.id = "waniwani-chat-embed";
	hostElement.style.cssText =
		"position:fixed;inset:0;z-index:2147483000;pointer-events:none;";
	document.body.appendChild(hostElement);

	const shadowRoot = hostElement.attachShadow({ mode: "open" });
	injectStyles(shadowRoot, config);

	const mountContainer = document.createElement("div");
	mountContainer.className = "ww:contents";
	shadowRoot.appendChild(mountContainer);

	const floatingRef = React.createRef<FloatingChatHandle>();

	reactRoot = ReactDOM.createRoot(mountContainer);
	reactRoot.render(
		React.createElement(FloatingChat, {
			ref: floatingRef,
			config,
			programmatic,
			scriptConfig,
		}),
	);

	return {
		destroy: () => {
			reactRoot?.unmount();
			reactRoot = null;
			hostElement?.remove();
			hostElement = null;
			currentInstance = null;
		},
		open: () => floatingRef.current?.open(),
		close: () => floatingRef.current?.close(),
		toggle: () => floatingRef.current?.toggle(),
		sendMessage: (text: string) => floatingRef.current?.sendMessage(text),
		sendMessageAndWait: async (text: string) => {
			if (!floatingRef.current) {
				return undefined;
			}
			return floatingRef.current.sendMessageAndWait(text);
		},
		reset: () => floatingRef.current?.reset(),
		restart: () => floatingRef.current?.restart(),
		copyConversation: async () =>
			(await floatingRef.current?.copyConversation()) ?? false,
		focus: () => floatingRef.current?.focus(),
		getMessages: () => floatingRef.current?.getMessages() ?? [],
		getSessionId: () => floatingRef.current?.getSessionId(),
	};
}

// ---------------------------------------------------------------------------
// Mount — off
// ---------------------------------------------------------------------------

// `mode: "off"` — nothing on screen. No host element, no shadow root, no React
// root: the chat is not mounted, so the visitor sees no dock and no inline box.
// What survives is everything that isn't the widget: `init()` still starts
// WebMCP (a browsing agent standing on the page gets the site's tools) and
// still hands the page a live `WaniWani.chat.track` / `.identify`. The chat
// methods answer as no-ops rather than throwing, so host code written against
// a visible embed keeps running when the tag is switched off.
function mountOff(): MountedEmbed {
	return {
		destroy: () => {
			currentInstance = null;
		},
		open: () => {},
		close: () => {},
		toggle: () => {},
		sendMessage: () => {},
		sendMessageAndWait: async () => undefined,
		reset: () => {},
		restart: () => {},
		copyConversation: async () => false,
		focus: () => {},
		getMessages: () => [],
		getSessionId: () => undefined,
	};
}

// ---------------------------------------------------------------------------
// Mount — composer
// ---------------------------------------------------------------------------

function mountComposer(
	config: EmbedConfig,
	programmatic: Partial<EmbedConfig> | undefined,
	scriptConfig: Partial<EmbedConfig> | undefined,
	scriptEl: HTMLScriptElement | null,
): MountedEmbed {
	// The composer itself mounts in the page flow, like the inline embed: same
	// `[data-waniwani-embed]` contract, auto-created in front of the script tag
	// when the page has none.
	const container = ensureInlineContainer(scriptEl);

	// No `SIZING_DEFAULTS_CSS` and no `data-height` here: the composer is one
	// input, sized by its content, so the inline embed's 500px default would
	// leave a tall empty box. Container chrome is skipped too — the composer
	// draws its own border, radius and shadow, and a second set around it would
	// read as a box inside a box. `applyContainerAppearance` still runs so
	// `appearance.variables.boxShadow` / `borderColor` reach the composer: CSS
	// custom properties inherit into the shadow root through the composed tree.
	if (container.hasAttribute(AUTO_MARKER_ATTR)) {
		ensureStyle(COMPOSER_AUTO_STYLE_ID, COMPOSER_AUTO_CSS);
	}
	applyContainerAppearance(container, config);

	hostElement = document.createElement("div");
	hostElement.id = "waniwani-chat-embed";
	hostElement.style.cssText = "display:block;width:100%;";
	container.appendChild(hostElement);

	const shadowRoot = hostElement.attachShadow({ mode: "open" });
	injectStyles(shadowRoot, config);

	const mountContainer = document.createElement("div");
	mountContainer.className = "ww:contents";
	shadowRoot.appendChild(mountContainer);

	// Viewport-sized, click-through overlay for the panel — the same host
	// `floating` mode uses, and for the same reason: the panel is fixed to the
	// viewport, and the host page must stay interactive everywhere the panel
	// isn't. It gets its own shadow root (styles are scoped per root), and the
	// mount node below is the portal target the composer renders the panel into.
	panelHostElement = document.createElement("div");
	panelHostElement.id = "waniwani-chat-panel";
	panelHostElement.style.cssText =
		"position:fixed;inset:0;z-index:2147483000;pointer-events:none;";
	document.body.appendChild(panelHostElement);

	const panelShadowRoot = panelHostElement.attachShadow({ mode: "open" });
	injectStyles(panelShadowRoot, config);

	const panelContainer = document.createElement("div");
	panelContainer.className = "ww:contents";
	panelShadowRoot.appendChild(panelContainer);

	const composerRef = React.createRef<ComposerChatHandle>();

	// Collapse the container (which we own, outside React) on gated pages so it
	// shows no empty box. Pre-hide synchronously from the sessionStorage cache
	// so repeat visits to a gated page don't flash before the fetch resolves;
	// `onVisibilityChange` then keeps it in sync (incl. SPA route changes).
	const toggleContainer = (vis: boolean) => {
		if (container instanceof HTMLElement) {
			container.style.display = vis ? "" : "none";
		}
	};
	if (config.token) {
		const cached = loadCachedConfig(
			config.api ?? "",
			config.token,
			config.channelId,
		);
		if (
			cached?.visibility &&
			!isVisibleForPath(cached.visibility, window.location.pathname)
		) {
			toggleContainer(false);
		}
	}

	reactRoot = ReactDOM.createRoot(mountContainer);
	reactRoot.render(
		React.createElement(ComposerChat, {
			ref: composerRef,
			config,
			programmatic,
			scriptConfig,
			panelContainer,
			onVisibilityChange: toggleContainer,
		}),
	);

	return {
		destroy: () => {
			reactRoot?.unmount();
			reactRoot = null;
			hostElement?.remove();
			hostElement = null;
			panelHostElement?.remove();
			panelHostElement = null;
			// Remove the container too, but only if WE created it — leave any
			// author-placed `[data-waniwani-embed]` element in the page so
			// `destroy()` doesn't strip the host's own markup.
			if (container.hasAttribute(AUTO_MARKER_ATTR)) {
				container.remove();
			}
			currentInstance = null;
		},
		open: () => composerRef.current?.open(),
		close: () => composerRef.current?.close(),
		toggle: () => composerRef.current?.toggle(),
		sendMessage: (text: string) => composerRef.current?.sendMessage(text),
		sendMessageAndWait: async (text: string) => {
			if (!composerRef.current) {
				return undefined;
			}
			return composerRef.current.sendMessageAndWait(text);
		},
		reset: () => composerRef.current?.reset(),
		restart: () => composerRef.current?.restart(),
		copyConversation: async () =>
			(await composerRef.current?.copyConversation()) ?? false,
		focus: () => composerRef.current?.focus(),
		getMessages: () => composerRef.current?.getMessages() ?? [],
		getSessionId: () => composerRef.current?.getSessionId(),
	};
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function init(options?: Partial<EmbedConfig>): EmbedInstance {
	if (currentInstance) {
		console.warn(
			"[Waniwani] Chat widget is already initialized. Call destroy() first to re-initialize.",
		);
		return currentInstance;
	}

	// Parse `data-*` and capture the script element once synchronously —
	// `document.currentScript` is only valid during script execution, so we
	// must grab both here and thread them through (the element is needed to
	// auto-create an inline container in front of it).
	const scriptEl = findScriptTag();
	const scriptConfig = parseConfigFromScript();
	const config = resolveConfig(options, undefined, scriptConfig);

	// Seed a host-supplied visitor id before mount so the very first chat
	// request and the host-page tracking client both pick it up. Accepts a
	// literal or a sync/async resolver; an async one applies once it settles.
	// For an id that resolves later still, callers use
	// `WaniWani.chat.setVisitorId()`.
	applyVisitorId(config.visitorId);

	// The site's own tools, published to a browsing agent standing on the page.
	// Started before the chat mounts so the listing is on the wire first.
	// Independent of the chat: it mounts its own root, and a widget step reaches
	// the visitor whether the panel is open or not. Returns null on every browser
	// without `modelContext`, which is nearly all of them.
	let webmcp: WebMcpHandle | null = null;
	try {
		webmcp = startWebMcp(config);
	} catch (error) {
		console.error("[Waniwani] WebMCP setup failed", error);
	}

	let beacon: { stop: () => void } | null = null;
	if (config.agentBeacon === true && config.token) {
		try {
			beacon = startAgentBeacon({
				api: config.api ?? "",
				token: config.token,
			});
		} catch (error) {
			console.error("[Waniwani] Agent beacon setup failed", error);
		}
	}

	let mounted: MountedEmbed;
	if (config.mode === "off") {
		mounted = mountOff();
	} else if (config.mode === "floating") {
		mounted = mountFloating(config, options, scriptConfig);
	} else if (config.mode === "composer") {
		mounted = mountComposer(config, options, scriptConfig, scriptEl);
	} else {
		mounted = mountInline(config, options, scriptConfig, scriptEl);
	}

	// Host-page tracking rides on the same public token and channel the chat
	// itself uses; the session id attaches live once the first exchange
	// assigns one.
	const api = config.api ?? "";
	const trackClient = config.token
		? createChatTrackClient({
				api,
				token: config.token,
				// Host-supplied channel wins; a token-only embed reads the
				// server-resolved channel from the cached `/config`, same as
				// `getSource` below.
				channelId: () =>
					config.channelId ??
					loadCachedConfig(api, config.token, config.channelId)?.channelId,
				getSource: () =>
					loadCachedConfig(api, config.token, config.channelId)?.source ??
					undefined,
				getSessionId: () => mounted.getSessionId(),
			})
		: createNoopChatTrackClient(
				"no public token configured (set data-token or pass token to init())",
			);

	currentInstance = {
		...mounted,
		track: trackClient.track,
		identify: (userId, traits) => trackClient.identify(userId, traits),
		setVisitorId: (id) => setVisitorId(id),
		getVisitorId: () => getOrCreateVisitorId(),
		destroy: () => {
			void trackClient.shutdown();
			webmcp?.destroy();
			beacon?.stop();
			mounted.destroy();
		},
	};

	// The top-of-funnel `page.viewed` event is fired from `useRemoteEmbedConfig`
	// once the channel's `/config` resolves, so it carries the channel's source.

	return currentInstance;
}

function destroy(): void {
	if (!currentInstance) {
		return;
	}
	currentInstance.destroy();
}

// ---------------------------------------------------------------------------
// Expose global API
// ---------------------------------------------------------------------------

// Calls made before `init()` warn once and are discarded, instead of crashing
// the host page.
const uninitializedTrack = createNoopChatTrackClient(
	"chat widget is not initialized (call WaniWani.chat.init() first)",
);
const liveTrack = (): TrackFn =>
	currentInstance ? currentInstance.track : uninitializedTrack.track;

window.WaniWani = window.WaniWani || {};
window.WaniWani.chat = {
	init,
	destroy,
	open: () => currentInstance?.open(),
	close: () => currentInstance?.close(),
	toggle: () => currentInstance?.toggle(),
	sendMessage: (text: string) => currentInstance?.sendMessage(text),
	sendMessageAndWait: async (text: string) => {
		if (!currentInstance) {
			return undefined;
		}
		return currentInstance.sendMessageAndWait(text);
	},
	reset: () => currentInstance?.reset(),
	restart: () => currentInstance?.restart(),
	copyConversation: async () =>
		(await currentInstance?.copyConversation()) ?? false,
	focus: () => currentInstance?.focus(),
	getMessages: () => currentInstance?.getMessages() ?? [],
	getSessionId: () => currentInstance?.getSessionId(),
	track: Object.assign((event: TrackInput) => liveTrack()(event), {
		priceShown: (input: Parameters<TrackFn["priceShown"]>[0]) =>
			liveTrack().priceShown(input),
		pricesCompared: (input: Parameters<TrackFn["pricesCompared"]>[0]) =>
			liveTrack().pricesCompared(input),
		optionSelected: (input: Parameters<TrackFn["optionSelected"]>[0]) =>
			liveTrack().optionSelected(input),
		leadQualified: (input?: Parameters<TrackFn["leadQualified"]>[0]) =>
			liveTrack().leadQualified(input),
		converted: (input: Parameters<TrackFn["converted"]>[0]) =>
			liveTrack().converted(input),
	}),
	identify: (userId: string, traits?: Record<string, unknown>) =>
		currentInstance
			? currentInstance.identify(userId, traits)
			: uninitializedTrack.identify(userId, traits),
	// Visitor-id helpers operate on `localStorage`, so they work before
	// `init()` too — a page can pin the id as soon as its analytics SDK is
	// ready, whether or not the chat has mounted.
	setVisitorId: (id: string) => setVisitorId(id),
	getVisitorId: () => getOrCreateVisitorId(),
};

// ---------------------------------------------------------------------------
// Auto-init from script tag data attributes
// ---------------------------------------------------------------------------

function autoInit(): void {
	const scriptConfig = parseConfigFromScript();
	if (!scriptConfig.token) {
		return;
	}

	try {
		init();
	} catch (err) {
		console.error("[Waniwani] Auto-initialization failed:", err);
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", autoInit);
} else {
	// DOM is already ready (script loaded with defer/async or injected late)
	autoInit();
}
